import React, {useRef, useState} from 'react';
import {analyticsText} from '../../analyticsText';
import Tooltip from '../../../../uiToolkit/Tooltip';
import {hasValue} from '../../../../utils/typecheck';
import {getFirstCapitals} from '../../../../utils/getFirstCapitals';
import './styles.css';
import {truncateNumberTo2Decimals} from '../../../../utils/getTimeTextFromSeconds';

export interface TableHeaderItem {
  title: string;
  id: string;
}

export interface AvgScores {
  scores: {
    [itemId: string]: {
      grade?: number;
      status: number;
    };
  };
  overallAvgScore: number;
}

export interface LearnersScores {
  scores: {
    [itemId: string]: {
      grade?: number;
      status: number;
      hasStarted: boolean;
    };
  };
  id: string;
  name: string;
  avgGrade?: string;
  avatarUrl?: string;
}

export const GRADE_SUCCESS = 2;
export const GRADE_FAIL = 3;
export const GRADE_PROGRESS = 1;

interface Props {
  isLoading: boolean;
  loadNextPage?: () => Promise<void>;
  orderAsc: boolean;
  setOrderAsc: (value: boolean) => void;
  headerItems: TableHeaderItem[];
  avgScores: AvgScores;
  learnerRecords: LearnersScores[];
}

const GradesTable = React.memo(
  ({
    isLoading,
    loadNextPage,
    orderAsc,
    setOrderAsc,
    headerItems,
    avgScores,
    learnerRecords,
  }: Props) => {
    const [selectedColumn, setSelectedColumn] = useState('');
    const [selectedRow, setSelectedRow] = useState('');

    const observer = useRef<any>();

    const overscrollLock = useRef(false);

    const lastPostElementRef = (node: any) => {
      if (isLoading || overscrollLock.current || !loadNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          overscrollLock.current = true;
          loadNextPage().then(() => {
            overscrollLock.current = false;
          });
        }
      });

      if (node) observer.current.observe(node);
    };

    const clearSelectedRowCol = () => {
      setSelectedColumn('');
      setSelectedRow('');
    };

    const onSelectColumn = (value: string) => (e: any) => {
      e.stopPropagation();
      setSelectedColumn(value);
    };

    const onSelectRow = (value: string) => (e: any) => {
      e.stopPropagation();
      setSelectedRow(value);
    };

    const calculateAvgScore = (learnerRecord: LearnersScores) => {
      let summ = 0;
      let count = 0;
      Object.keys(learnerRecord.scores).forEach(key => {
        if (hasValue(learnerRecord.scores[key]?.grade)) {
          summ += learnerRecord.scores[key].grade!;
          count++;
        }
      });
      if (count > 0) {
        return truncateNumberTo2Decimals(summ / count) + '%';
      }
      return <div className="NoResultsDash" />;
    };

    const renderGradeByState = (
      state?: number,
      grade?: number,
      hasStarted?: boolean,
    ) => {
      if (hasStarted === false && (state === 1 || state === 2)) {
        return <div className="GradeNotStartedBox" />;
      }
      //inProgress
      if (state === 1) {
        return <div className="GradeProgressBox" />;
      }
      //success
      if (state === 2) {
        return (
          <div className="GradeSuccessBox">
            {hasValue(grade) ? (
              truncateNumberTo2Decimals(grade!) + '%'
            ) : (
              <span className="icon-typeCheck"></span>
            )}
          </div>
        );
      }
      //fail
      if (state === 3) {
        return (
          <div className="GradeFailedBox">
            {hasValue(grade) ? truncateNumberTo2Decimals(grade!) + '%' : ''}
          </div>
        );
      }
      return <div className="NoResultsDash" />;
    };

    return (
      <table className="GradesTable" onClick={clearSelectedRowCol}>
        <thead>
          <tr className="GradebookHeader">
            <th
              className="SortHeader"
              onClick={() => {
                setOrderAsc(!orderAsc);
              }}>
              <div className="SortArrowContainer">
                {analyticsText.sortBy}
                <div className={orderAsc ? 'arrow-down' : 'arrow-up'} />
              </div>
            </th>
            <th className="AvgScoreColumn">{analyticsText.avgScoreHeader}</th>
            {headerItems.map(item => (
              <th
                key={item.id}
                className="LessonHeader"
                onClick={onSelectColumn(item.id)}>
                <Tooltip tooltipText={item.title}>
                  <p>{item.title}</p>
                </Tooltip>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="CourseAvgRow">
            <td className="OverallHeader">{analyticsText.overallScore}</td>
            <td className="AvgScoreColumn AvgSuccess">
              {hasValue(avgScores.overallAvgScore) ? (
                truncateNumberTo2Decimals(avgScores.overallAvgScore) + '%'
              ) : (
                <div className="NoResultsDash" />
              )}
            </td>
            {headerItems.map(item => (
              <td key={item.id} className="GradeCell">
                {renderGradeByState(
                  avgScores.scores[item.id]?.status,
                  avgScores.scores[item.id]?.grade,
                  true,
                )}
              </td>
            ))}
          </tr>
          {learnerRecords.map((record, index) => (
            <tr
              key={record.id}
              className={`${selectedRow === record.id ? 'SelectedRowCol' : ''}`}
              ref={
                learnerRecords.length === index + 1 ? lastPostElementRef : null
              }>
              <td className={'LearnerName'} onClick={onSelectRow(record.id)}>
                <div className="LearnerNameContainer">
                  {record.avatarUrl ? (
                    <img src={record.avatarUrl} />
                  ) : (
                    <div className="AvatarPlaceholder">
                      {getFirstCapitals(record.name)}
                    </div>
                  )}
                  <Tooltip tooltipText={record.name}>
                    <span>{record.name}</span>
                  </Tooltip>
                </div>
              </td>
              <td className="AvgScoreColumn AvgSuccess">
                {calculateAvgScore(record)}
              </td>
              {headerItems.map(item => (
                <td
                  key={item.id}
                  className={
                    selectedColumn === item.id ? 'SelectedRowCol' : ''
                  }>
                  {renderGradeByState(
                    record.scores[item.id]?.status,
                    record.scores[item.id]?.grade,
                    record.scores[item.id]?.hasStarted,
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  },
);

export default GradesTable;
